import { PropsWithChildren } from 'react';
import styles from './styles.module.scss';
import { ErrorCard } from '@starsoft/common/components';
import { useInfiniteScroll, useWithdrawForm } from '@starsoft/common/hooks';
import AmountInput from '../../AmountInput';
import WithdrawalMethods from './WithdrawalMethods';
import WithdrawCoinSelect from './CoinSelect';
import { useFormContext } from 'react-hook-form';
import { WithdrawFormCreateDto } from '../../../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import TransactionsModalWithdrawContentFormLayoutNoMethodsAvailable from './NoMethodsAvailable';

/**
 * TransactionsModalWithdrawContentFormLayout Component
 * This component is responsible for rendering the layout of the withdrawal form, including handling errors and infinite scrolling.
 *
 * @param {PropsWithChildren} props - The properties passed to the component, including children elements.
 * @returns {JSX.Element} The layout of the withdrawal form, including coin selection, amount input, and withdrawal methods.
 *
 * @example
 * // Usage example:
 * <TransactionsModalWithdrawContentFormLayout>
 *   <CustomChildComponent />
 * </TransactionsModalWithdrawContentFormLayout>
 */
export default function TransactionsModalWithdrawContentFormLayout({
  children,
}: PropsWithChildren) {
  // Retrieve form context methods for watching and setting form values
  const { watch, setValue } = useFormContext<WithdrawFormCreateDto>();

  // Use custom hook to manage withdrawal form state and methods
  const {
    formError,
    refetchForm,
    withdrawMethodsQuery: {
      withdrawMethods,
      hasNextPage,
      isPendingWithdrawalMethods,
      isFetchingNextPage,
      fetchNextPage,
    },
  } = useWithdrawForm(watch, setValue);

  // Use custom hook for infinite scrolling functionality
  const { ref } = useInfiniteScroll({
    hasMore: hasNextPage,
    isLoading: isPendingWithdrawalMethods || isFetchingNextPage,
    loadMore: fetchNextPage,
  });

  // Render an error card if there is a form error
  if (formError) {
    return <ErrorCard error={formError} refetch={refetchForm} />;
  }

  // Render the form layout with coin selection, amount input, and withdrawal methods
  return (
    <div className={styles.container}>
      <div className={styles.container__content} ref={ref}>
        <WithdrawCoinSelect />
        {(!isPendingWithdrawalMethods && withdrawMethods?.length > 0 && (
          <>
            <AmountInput />
            <WithdrawalMethods />
            {children}
          </>
        )) || <TransactionsModalWithdrawContentFormLayoutNoMethodsAvailable />}
      </div>
    </div>
  );
}
